import { useCallback, useEffect, useRef } from "react";

import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import PageContainer, { SECTION_TABLE_STYLES } from "components/PageContainer";
import SidebarLayout from "components/SidebarLayout";
import setPageTitle from "helpers/title";
import { useDataJobTable } from "hooks/useDataJobTable";
import AuthService from "services/Auth";
import useAuthorized from "hooks/useAuthorized";
import ACL_RELATIONSHIPS from "acl-relationships";
import CONSTANTS from "constants/Constants";
import DataJobService from "services/DataJob";
import { getNextQueryParams } from "components/helpers";
import DataSidebar from "components/sidebar/DataSidebar";
import { ExportModal, ImportModal } from "./components/Modals";

const { EXPORT_TYPES, IMPORT_TYPES } = CONSTANTS;

export const DATA_JOB_TABLE_CONSTANTS = {
  tableName: "DataJobs",
  queryKey: "data-job",
  className: "DataJob",
  exportTableName: "DataJobsTable",
  header: "Data Jobs",
  headerIcon: "clone",
  pageTitle: "Data",
  // Job type prefixes
  JOB_TYPE_PREFIXES: {
    EXPORT: "EXPORT",
    IMPORT: "IMPORT",
    VALIDATION: "VALIDATION",
  },
  // Job type strings
  JOB_TYPES: {
    CASE_DATA_IMPORT: "CASE DATA IMPORT",
  },
  // Filter keys
  FILTER_KEYS: {
    STATUS: "status",
    MODEL: "model",
    ACTION: "action",
    CREATOR_USER_ID: "creator_user_id",
    JOB_TYPE: "job_type",
    CREATED_DATE: "created_date",
    TAG_ID: "tag_id",
  },
  // Filter model types
  FILTER_MODEL_TYPES: {
    AUDIT_LOG: "audit_log",
    CONTACT: "contact",
    CAMPAIGN_TARGET: "campaign_target",
  },
  // Filter action types
  FILTER_ACTION_TYPES: {
    IMPORT: "IMPORT",
    EXPORT: "EXPORT",
  },
  // Column sizes
  COLUMN_SIZES: {
    SELECT: 50,
    ID: 60,
    CREATED_AT: 130,
    STATUS: 100,
    ACTIONS: 60,
    PROGRESS: 80,
  },
  // Display text
  DISPLAY_TEXT: {
    NO_FILE_NAME: "No File Name",
    NO_LINK: "No Link",
    EXPORT_PLACEHOLDER: "No Result",
    CHECK_RESULT: "Check Result",
    DOWNLOAD_HERE: "Download",
    ABORTED: "aborted",
  },
  // Date format
  DATE_FORMAT: {
    FORMAT: "YYYY-MM-DD HH:mm",
  },
  // Status text
  STATUS_TEXT: {
    ABORTED: "aborted",
  },
};

function DataJobTable() {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.dataJobTags.create,
      ACL_RELATIONSHIPS.dataJobTags.read,
      ACL_RELATIONSHIPS.dataJobTags.delete,
    ],
    { partialMatch: true }
  );

  const {
    filters,
    columns,
    invalidateDataJobsQuery,
    importModal,
    caseDataImportModal,
    openImportModal,
    openCaseDataImportModal,
  } = useDataJobTable({ tableName: DATA_JOB_TABLE_CONSTANTS.tableName });

  const exportModal = useRef(null);
  const openExportModal = type => exportModal.current?.open(type);

  useEffect(() => {
    setPageTitle(DATA_JOB_TABLE_CONSTANTS.pageTitle);
  }, []);

  const fetchData = useCallback(
    async queryParams => {
      if (!AuthService.isLoggedIn()) {
        return { data: [], total: 0 };
      }
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response = await DataJobService.getDataJobs(nextQueryParams);
        return response;
      } catch (error) {
        console.error("Error fetching data jobs:", error);
        throw error;
      }
    },
    [columns]
  );

  return (
    <SidebarLayout
      mobileMenuLabel="Data Navigation"
      sidebar={
        <DataSidebar
          onImportData={() => openImportModal()}
          onImportCaseData={() => openCaseDataImportModal()}
          onExportContacts={() => openExportModal(EXPORT_TYPES.CONTACTS)}
          onExportTargets={() => openExportModal(EXPORT_TYPES.CAMPAIGN_TARGETS)}
          onExportAuditLog={() => openExportModal(EXPORT_TYPES.AUDIT_LOGS)}
        />
      }
      content={
        <>
          <ImportModal ref={importModal} fetchData={invalidateDataJobsQuery} />
          <ImportModal
            importType={IMPORT_TYPES.CASE_DATA}
            ref={caseDataImportModal}
            fetchData={invalidateDataJobsQuery}
          />
          <ExportModal ref={exportModal} fetchData={invalidateDataJobsQuery} />
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <BaseTableV3
              columns={columns}
              filters={filters}
              fetchData={fetchData}
              headerIcon={DATA_JOB_TABLE_CONSTANTS.headerIcon}
              header={DATA_JOB_TABLE_CONSTANTS.header}
              tableName={DATA_JOB_TABLE_CONSTANTS.tableName}
              queryKey={DATA_JOB_TABLE_CONSTANTS.queryKey}
              className={DATA_JOB_TABLE_CONSTANTS.className}
              enableSearch={false}
              enableSettings
              tagsDropdownParams={{ visible: enableTags }}
              invalidateData={invalidateDataJobsQuery}
            />
          </PageContainer>
        </>
      }
    />
  );
}

export default DataJobTable;
